import React, { useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, Text, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { apiEventBus, type ApiErrorEvent } from 'src/core/events/Events';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';

// How long the toast stays up, and the minimum gap between two toasts so a burst of failing calls (the common
// case when the server is unreachable) collapses into one.
const VISIBLE_MS = 3500;
const THROTTLE_MS = 4000;

/**
 * Global toast for failed API calls.
 */
export default function ApiErrorToast() {
	const insets = useSafeAreaInsets();
	const [message, setMessage] = useState<string | null>(null);
	const [kind, setKind] = useState<ApiErrorEvent['kind']>('network');
	const opacity = useRef(new Animated.Value(0)).current;
	const lastShownRef = useRef(0);
	// Set when a toast is shown, cleared on the next successful API call.
	const shownSinceSuccess = useRef(false);
	const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const hide = () => {
			Animated.timing(opacity, { toValue: 0, duration: 200, useNativeDriver: true }).start(() => setMessage(null));
		};

		const show = (...args: unknown[]) => {
			const event = (args[0] ?? {}) as ApiErrorEvent;

			// One toast per outage: after the first failure stay quiet until a call succeeds again (the success
			// listener re-arms), so switching tabs during an outage does not re-toast.
			if (shownSinceSuccess.current) return;

			const now = Date.now();
			if (now - lastShownRef.current < THROTTLE_MS) return;
			lastShownRef.current = now;
			shownSinceSuccess.current = true;

			setKind(event.kind);
			setMessage(event.kind === 'server' ? Translations.errorServer : Translations.errorNoConnection);
			Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }).start();

			if (hideTimer.current) clearTimeout(hideTimer.current);
			hideTimer.current = setTimeout(hide, VISIBLE_MS);
		};

		// A successful call means we recovered; re-arm so a later failure can alert again.
		const rearm = () => {
			shownSinceSuccess.current = false;
		};

		const offError = apiEventBus.on(Constants.eventApiOnError, show);
		const offSuccess = apiEventBus.on(Constants.eventApiOnSuccess, rearm);
		return () => {
			offError();
			offSuccess();
			if (hideTimer.current) clearTimeout(hideTimer.current);
		};
	}, [opacity]);

	if (!message) return null;

	const dismiss = () => {
		if (hideTimer.current) clearTimeout(hideTimer.current);
		Animated.timing(opacity, { toValue: 0, duration: 200, useNativeDriver: true }).start(() => setMessage(null));
	};

	const isServer = kind === 'server';

	return (
		<Animated.View
			pointerEvents="box-none"
			style={[styles.wrapper, { bottom: insets.bottom + Dimens.margin20, opacity }]}
		>
			<TouchableOpacity
				accessibilityRole="alert"
				activeOpacity={0.9}
				style={[styles.toast, { backgroundColor: isServer ? Colors.redError : Colors.orange }]}
				testID="api-error-toast"
				onPress={dismiss}
			>
				<Ionicons
					name={isServer ? 'alert-circle-outline' : 'cloud-offline-outline'}
					size={20}
					style={[styles.icon, { color: isServer ? Colors.white : Colors.black }]}
				/>
				<Text style={[styles.text, { color: isServer ? Colors.white : Colors.black }]}>{message}</Text>
			</TouchableOpacity>
		</Animated.View>
	);
}

const styles = StyleSheet.create({
	wrapper: {
		position: 'absolute',
		left: Dimens.margin16,
		right: Dimens.margin16,
		zIndex: 999,
	},
	toast: {
		flexDirection: 'row',
		alignItems: 'center',
		borderRadius: 10,
		paddingHorizontal: Dimens.padding16,
		paddingVertical: Dimens.padding12,
	},
	icon: {
		marginRight: Dimens.margin8,
	},
	text: {
		flex: 1,
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize14,
	},
});
